/**
 * CMS API Service - Complete Integration
 * Based on cms-api-doc.json - All CMS endpoints for grandPittu Admin
 */

import adminApi from './adminApi';

// ==================== TYPES ====================

export interface GlobalSettingDTO {
  siteName?: string;
  tagline: string;
  logoUrl: string;
  faviconUrl: string;
  contactPhone: string;
  contactEmail: string;
  contactAddress: string;
  locationLabel: string;
  googleMapEmbedUrl: string;
  weekdaysLabel: string;
  weekdaysHours: string;
  fridayLabel: string;
  fridayHours: string;
  socialLinks: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
    whatsapp?: string;
  };
  primaryColor: string;
  secondaryColor: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  ogImageUrl: string;
}

export interface HeroSliderDTO {
  subtitle?: string;
  title: string;
  titleLine2?: string;
  buttonText?: string;
  sortOrder?: number;
  active?: boolean;
}

export interface CategoryItem {
  id?: number;
  name: string;
  subName?: string;
  imageUrl?: string;
  _isNew?: boolean;
  _isEdited?: boolean;
  [key: string]: any; // Allow index signature for InlineArray component
}

export interface CategorySectionDTO {
  subtitle?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  categoryItem?: CategoryItem[];
  aboutSectionImagesDetails?: AboutSectionImageDTO[];
}

export interface AboutSectionImageDTO {
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
}

export interface AboutSectionDTO {
  sectionName: string;
  title: string;
  subTitle: string;
  description: string;
  bgImageUrl?: string;
  buttonName: string;
  videoUrl?: string;
  videoTitle: string;
  ctaSubTitle?: string;
  ctaTitle?: string;
  ctaButtonName?: string;
  ctaBgImageUrl?: string;
  images?: AboutSectionImageDTO[];
}

export interface RooftopItemDTO {
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
}

export interface RooftopSectionDTO {
  id?: number;
  pageName: string;
  title: string;
  subTitle: string;
  description: string;
  buttonName: string;
  imgUrl: string;
  contactSubTitle: string;
  contactTitle: string;
  ctaTitle: string;
  ctaSubTitle: string;
  ctaButtonText: string;
  ctaImgUrl: string;
  ctaDescription: string;
  items: RooftopItemDTO[];
  // Gallery fields
  gallerySubtitle?: string;
  galleryTitle?: string;
  galleryImages?: RooftopItemDTO[];
}

export interface MenuItemDTO {
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
}

export interface MenuShowcaseDTO {
  id?: number;
  sectionName: string;
  title: string;
  subtitle: string;
  buttonText: string;
  pdfUrl?: string;
  pdfFilePath?: string;
  images?: MenuItemDTO[];
}

export interface GalleryImageDTO {
  id?: number;
  name: string;
  subName: string;
  imageUrl: string;
}

export interface GallerySectionDTO {
  id?: number;
  title: string;
  subtitle: string;
  imageUrl: string;
  buttonText: string;
  sectionName?: string; // 'ROOFTOP' | 'GALLERY'
  images: GalleryImageDTO[];
}

export interface ReservationTimeSlotDTO {
  id?: number;
  time: string;
  active: boolean;
  dayOfWeek: 'WEEKDAY' | 'ALLDAYS' | 'WEEKEND';
}

export interface ReservationSectionDTO {
  id?: number;
  title: string;
  subtitle: string;
  successMsg: string;
  phone: boolean;
  email: boolean;
  timeSlots: ReservationTimeSlotDTO[];
}

// ==================== CMS API SERVICE ====================

class CMSApiService {
  // ==================== GLOBAL SETTINGS ====================

  /**
   * Add global settings with logo and favicon files
   */
  async addGlobalSetting(
    dto: GlobalSettingDTO,
    logo: File,
    favicon: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append('logo', logo);
    formData.append('favicon', favicon);
    formData.append('globalSettingRequestDto', JSON.stringify(dto));

    return adminApi.fetch('/globalSetting', {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Update global settings by ID with optional logo and favicon
   */
  async updateGlobalSetting(
    id: number,
    dto: Partial<GlobalSettingDTO>,
    logo?: File,
    favicon?: File
  ): Promise<any> {
    const formData = new FormData();
    if (logo) formData.append('logo', logo);
    if (favicon) formData.append('favicon', favicon);
    formData.append('globalSettingRequestDto', JSON.stringify(dto));

    return adminApi.fetch(`/globalSetting/${id}`, {
      method: 'PUT',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  /**
   * Get global setting by ID
   */
  async getGlobalSettingById(id: number): Promise<any> {
    return adminApi.fetch(`/globalSetting/${id}`);
  }

  /**
   * Get all global settings
   */
  async getAllGlobalSettings(): Promise<any> {
    return adminApi.fetch('/globalSetting/getAll');
  }

  // ==================== HERO SLIDERS (HOME PAGE) ====================

  /**
   * Add hero slider with image
   */
  async addHeroSlider(dto: HeroSliderDTO, image: File): Promise<any> {
    const formData = new FormData();
    // Use Blob with application/json type to ensure proper content type
    const jsonBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
    formData.append('heroSliderDTO', jsonBlob, 'heroSliderDTO.json');
    formData.append('image', image);

    return adminApi.uploadFormData('/hero-sliders', formData, 'POST');
  }

  /**
   * Update hero slider by ID
   */
  async updateHeroSlider(id: number, dto: HeroSliderDTO, image?: File): Promise<any> {
    const formData = new FormData();
    // Use Blob with application/json type to ensure proper content type
    const jsonBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
    formData.append('heroSliderDTO', jsonBlob, 'heroSliderDTO.json');
    if (image) formData.append('image', image);

    return adminApi.uploadFormData(`/hero-sliders/${id}`, formData, 'PUT');
  }

  /**
   * Delete hero slider by ID
   */
  async deleteHeroSlider(id: number): Promise<any> {
    return adminApi.fetch(`/hero-sliders/${id}`, { method: 'DELETE' });
  }

  /**
   * Get hero slider by ID
   */
  async getHeroSliderById(id: number): Promise<any> {
    return adminApi.fetch(`/hero-sliders/${id}`);
  }

  /**
   * Reorder hero sliders
   */
  async reorderHeroSliders(orderedIds: number[]): Promise<void> {
    return adminApi.fetch(`/hero-sliders/reorder`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: orderedIds,
    });
  }

  // ==================== CATEGORY SECTION (HOME PAGE) ====================

  /**
   * Add category section with image and items
   * POST /category-section
   * Uses FormData with categoryItemDTO (JSON) and optional image file
   */
  async addCategorySection(dto: CategorySectionDTO, image?: File): Promise<any> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
    formData.append('categoryItemDTO', jsonBlob, 'categoryItemDTO.json');
    if (image) formData.append('image', image);

    return adminApi.uploadFormData('/category-section', formData, 'POST');
  }

  /**
   * Update category section by ID
   * PUT /category-section/{id}
   * Uses FormData with categorySectionDTO (JSON), optional image file, and optional itemId
   */
  async updateCategorySection(id: number, dto: Partial<CategorySectionDTO>, image?: File, itemId?: number): Promise<any> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(dto)], { type: 'application/json' });
    formData.append('categorySectionDTO', jsonBlob, 'categorySectionDTO.json');
    if (image) formData.append('image', image);
    if (itemId) formData.append('itemId', String(itemId));

    return adminApi.uploadFormData(`/category-section/${id}`, formData, 'PUT');
  }

  /**
   * Get category section by ID
   * GET /category-section/{id}
   */
  async getCategorySectionById(id: number): Promise<any> {
    return adminApi.fetch(`/category-section/${id}`, {}, false); // Bypass cache
  }

  /**
   * Get all category sections
   * GET /category-section/getAll
   */
  async getAllCategorySections(): Promise<any> {
    return adminApi.fetch('/category-section/getAll', {}, false); // Bypass cache
  }

  /**
   * Delete category section by ID
   * DELETE /category-section/{id}
   */
  async deleteCategorySection(id: number): Promise<any> {
    return adminApi.fetch(`/category-section/${id}`, { method: 'DELETE' });
  }

  /**
   * Add multiple images/features to category section
   * PUT /category-section/add-images/{id}
   * Sends array of AboutSectionImageDTO with name, subName, imageUrl
   */
  async addCategorySectionImages(sectionId: number, images: AboutSectionImageDTO[]): Promise<any> {
    return adminApi.fetch(`/category-section/add-images/${sectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: images
    });
  }

  /**
   * Update category section feature/item details
   * PUT /category-section/features/{id}
   * Updates a single feature with name, subName, imageUrl
   */
  async updateCategorySectionFeature(featureId: number, dto: AboutSectionImageDTO): Promise<any> {
    return adminApi.fetch(`/category-section/features/${featureId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Delete category section feature/item
   * DELETE /category-section/features/{id}
   * Removes a single feature from the category section
   */
  async deleteCategorySectionFeature(featureId: number): Promise<any> {
    return adminApi.fetch(`/category-section/features/${featureId}`, { method: 'DELETE' });
  }

  // ==================== ABOUT SECTION (HOME PAGE) ====================

  /**
   * Add about section
   */
  async addAboutSection(dto: AboutSectionDTO): Promise<any> {
    return adminApi.fetch('/about-section', { method: 'POST', data: dto });
  }

  /**
   * Get about section by ID
   */
  async getAboutSectionById(id: number): Promise<any> {
    return adminApi.fetch(`/about-section/${id}`, {}, false); // Bypass cache
  }

  /**
   * Get all about sections
   */
  async getAllAboutSections(): Promise<any> {
    return adminApi.fetch('/about-section', {}, false); // Bypass cache
  }

  /**
   * Get about section by name
   */
  async getAboutSectionByName(sectionName: string): Promise<any> {
    return adminApi.fetch(`/about-section/by-name/${sectionName}`, {}, false); // Bypass cache
  }

  /**
   * Update about section by ID
   */
  async updateAboutSection(id: number, dto: Partial<AboutSectionDTO>): Promise<any> {
    return adminApi.fetch(`/about-section/${id}`, { method: 'PUT', data: dto });
  }

  /**
   * Delete about section by ID
   */
  async deleteAboutSection(id: number): Promise<any> {
    return adminApi.fetch(`/about-section/${id}`, { method: 'DELETE' });
  }

  /**
   * Add new service/feature to about section
   * POST /about-section/features?sectionId={id}
   */
  async addAboutSectionFeature(sectionId: number, dto: AboutSectionImageDTO): Promise<any> {
    return adminApi.fetch(`/about-section/features?sectionId=${sectionId}`, {
      method: 'POST',
      data: dto
    });
  }

  /**
   * Add multiple images/features to about section
   * PUT /about-section/add-images/{id}
   */
  async addAboutSectionImages(sectionId: number, images: AboutSectionImageDTO[]): Promise<any> {
    return adminApi.fetch(`/about-section/add-images/${sectionId}`, {
      method: 'PUT',
      data: images
    });
  }

  /**
   * Update about section image details
   */
  async updateAboutSectionImageDetails(featureId: number, dto: AboutSectionImageDTO): Promise<any> {
    return adminApi.fetch(`/about-section/features/${featureId}`, { method: 'PUT', data: dto });
  }

  /**
   * Delete about section image details
   */
  async deleteAboutSectionImageDetails(featureId: number): Promise<any> {
    return adminApi.fetch(`/about-section/features/${featureId}`, { method: 'DELETE' });
  }

  // ==================== ROOFTOP SECTION (HOME PAGE) ====================

  /**
   * Add rooftop section
   */
  async addRooftopSection(dto: RooftopSectionDTO): Promise<any> {
    return adminApi.fetch('/rooftop-items', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Get rooftop section by ID
   */
  async getRooftopSectionById(id: number): Promise<any> {
    return adminApi.fetch(`/rooftop-items/${id}`, {}, false); // Bypass cache
  }

  /**
   * Get all rooftop sections
   */
  async getAllRooftopSections(): Promise<any> {
    return adminApi.fetch('/rooftop-items', {}, false); // Bypass cache
  }

  /**
   * Get rooftop section by page name
   */
  async getRooftopSectionByPage(pageName: string): Promise<any> {
    return adminApi.fetch(`/rooftop-items/page/${pageName}`, {}, false); // Bypass cache
  }

  /**
   * Update rooftop section
   */
  async updateRooftopSection(id: number, dto: Partial<RooftopSectionDTO>): Promise<any> {
    return adminApi.fetch(`/rooftop-items/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Delete rooftop section
   */
  async deleteRooftopSection(id: number): Promise<any> {
    return adminApi.fetch(`/rooftop-items/${id}`, { method: 'DELETE' });
  }

  /**
   * Add multiple rooftop items to section
   * PUT /rooftop-items/add-images/{id}
   */
  async addRooftopItems(sectionId: number, items: RooftopItemDTO[]): Promise<any> {
    return adminApi.fetch(`/rooftop-items/add-images/${sectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: items
    });
  }

  /**
   * Update rooftop image details
   */
  async updateRooftopImageDetails(featureId: number, dto: RooftopItemDTO): Promise<any> {
    return adminApi.fetch(`/rooftop-items/features/${featureId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Delete rooftop image details
   */
  async deleteRooftopImageDetails(featureId: number): Promise<any> {
    return adminApi.fetch(`/rooftop-items/features/${featureId}`, { method: 'DELETE' });
  }

  // ==================== MENU SHOWCASE (HOME PAGE) ====================

  /**
   * Add menu showcase section
   * POST /menu-cards
   * Backend expects multipart/form-data with menuCardDTO part
   */
  async addMenuShowcase(dto: MenuShowcaseDTO): Promise<any> {
    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
    return adminApi.uploadFormData('/menu-cards', formData, 'POST');
  }

  /**
   * Get menu showcase by section name
   * GET /menu-cards/by-name/{sectionName}
   */
  async getMenuShowcaseByName(sectionName: string): Promise<any> {
    return adminApi.fetch(`/menu-cards/by-name/${sectionName}`, {}, false);
  }

  /**
   * Get menu showcase by ID
   * GET /menu-cards/{id}
   */
  async getMenuShowcaseById(id: number): Promise<any> {
    return adminApi.fetch(`/menu-cards/${id}`, {}, false);
  }

  /**
   * Get all menu showcases
   * GET /menu-cards
   */
  async getAllMenuShowcases(): Promise<any> {
    return adminApi.fetch('/menu-cards', {}, false);
  }

  /**
   * Update menu showcase section
   * PUT /menu-cards/{id}
   */
  async updateMenuShowcase(id: number, dto: Partial<MenuShowcaseDTO>): Promise<any> {
    const formData = new FormData();
    formData.append('data', new Blob([JSON.stringify(dto)], { type: 'application/json' }));
    return adminApi.uploadFormData(`/menu-cards/${id}`, formData, 'PUT');
  }

  /**
   * Update menu showcase PDF only
   * PUT /menu-cards/{id}/pdf
   * Expects multipart/form-data with id and pdfFile
   */
  async updateMenuShowcasePdf(id: number, pdfFile: File): Promise<any> {
    const formData = new FormData();
    formData.append('id', String(id));
    formData.append('pdfFile', pdfFile);
    return adminApi.uploadFormData(`/menu-cards/${id}/pdf`, formData, 'PUT');
  }

  /**
   * Delete menu showcase PDF only
   * DELETE /menu-cards/{id}/pdf
   * Removes the PDF file from a menu card section
   */
  async deleteMenuShowcasePdf(id: number): Promise<any> {
    return adminApi.fetch(`/menu-cards/${id}/pdf`, { method: 'DELETE' });
  }

  /**
   * Delete menu showcase section
   * DELETE /menu-cards/{id}
   */
  async deleteMenuShowcase(id: number): Promise<any> {
    return adminApi.fetch(`/menu-cards/{id}`, { method: 'DELETE' });
  }

  /**
   * Add multiple menu items to menu showcase
   * PUT /menu-cards/add-images/{id}
   */
  async addMenuShowcaseItems(sectionId: number, items: MenuItemDTO[]): Promise<any> {
    return adminApi.fetch(`/menu-cards/add-images/${sectionId}`, {
      method: 'PUT',
      data: items
    });
  }

  /**
   * Update menu showcase item details
   * PUT /menu-cards/features/{id}
   */
  async updateMenuShowcaseItem(itemId: number, dto: MenuItemDTO): Promise<any> {
    return adminApi.fetch(`/menu-cards/features/${itemId}`, {
      method: 'PUT',
      data: dto
    });
  }

  /**
   * Delete menu showcase item
   * DELETE /menu-cards/features/{id}
   */
  async deleteMenuShowcaseItem(itemId: number): Promise<any> {
    return adminApi.fetch(`/menu-cards/features/${itemId}`, { method: 'DELETE' });
  }

  // ==================== GALLERY ====================

  /**
   * Add gallery section
   */
  async addGallerySection(dto: GallerySectionDTO): Promise<any> {
    return adminApi.fetch('/gallery', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Get gallery section by ID
   */
  async getGallerySectionById(id: number): Promise<any> {
    return adminApi.fetch(`/gallery/${id}`, {}, false); // Bypass cache
  }

  /**
   * Get all gallery sections
   */
  async getAllGallerySections(): Promise<any> {
    return adminApi.fetch('/gallery', {}, false); // Bypass cache
  }

  /**
   * Get gallery section by section name
   * GET /gallery/by-name/{sectionName}
   * @param sectionName - 'ROOFTOP' or 'GALLERY'
   */
  async getGallerySectionByName(sectionName: string): Promise<any> {
    return adminApi.fetch(`/gallery/by-name/${sectionName}`, {}, false);
  }

  /**
   * Update gallery section
   */
  async updateGallerySection(id: number, dto: Partial<GallerySectionDTO>): Promise<any> {
    return adminApi.fetch(`/gallery/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Add multiple images to gallery section
   * PUT /gallery/add-images/{id}
   */
  async addGalleryImages(sectionId: number, images: GalleryImageDTO[]): Promise<any> {
    return adminApi.fetch(`/gallery/add-images/${sectionId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: images
    });
  }

  /**
   * Update gallery image details
   */
  async updateGalleryImageDetails(featureId: number, dto: GalleryImageDTO): Promise<any> {
    return adminApi.fetch(`/gallery/features/${featureId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      data: dto
    });
  }

  /**
   * Delete gallery image details
   */
  async deleteGalleryImageDetails(featureId: number): Promise<any> {
    return adminApi.fetch(`/gallery/features/${featureId}`, { method: 'DELETE' });
  }

  /**
   * Delete gallery section
   */
  async deleteGallerySection(id: number): Promise<any> {
    return adminApi.fetch(`/gallery/${id}`, { method: 'DELETE' });
  }

  // ==================== IMAGE UPLOAD ====================

  /**
   * Upload image to specific folder
   */
  async uploadImage(image: File, folderName: string): Promise<any> {
    const formData = new FormData();
    formData.append('img', image);
    formData.append('folderName', folderName);

    return adminApi.fetch('/globalSetting/uploadImage', {
      method: 'POST',
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }

  // ==================== RESERVATION SECTION ====================

  /**
   * Add reservation section
   */
  async addReservationSection(dto: ReservationSectionDTO): Promise<any> {
    return adminApi.fetch('/reservationSection', { method: 'POST', data: dto });
  }

  /**
   * Get reservation section by ID
   */
  async getReservationSectionById(id: number): Promise<any> {
    return adminApi.fetch(`/reservationSection/${id}`);
  }

  /**
   * Get all reservation sections
   */
  async getAllReservationSections(): Promise<any> {
    return adminApi.fetch('/reservationSection');
  }

  /**
   * Update reservation section
   */
  async updateReservationSection(id: number, dto: Partial<ReservationSectionDTO>): Promise<any> {
    return adminApi.fetch(`/reservationSection/${id}`, { method: 'PUT', data: dto });
  }

  // ==================== TIME SLOTS ====================

  /**
   * Create time slot
   */
  async createTimeSlot(sectionId: number, dto: ReservationTimeSlotDTO): Promise<any> {
    return adminApi.fetch(`/reservationSection/timeSlots/${sectionId}`, { method: 'POST', data: dto });
  }

  /**
   * Get all time slots
   */
  async getAllTimeSlots(): Promise<any> {
    return adminApi.fetch('/reservationSection/timeSlots');
  }

  /**
   * Get all active time slots
   */
  async getAllActiveTimeSlots(): Promise<any> {
    return adminApi.fetch('/reservationSection/timeSlots/getActiveTime');
  }

  /**
   * Update time slot
   */
  async updateTimeSlot(id: number, dto: ReservationTimeSlotDTO): Promise<any> {
    return adminApi.fetch(`/reservationSection/timeSlots/${id}`, { method: 'PUT', data: dto });
  }

  /**
   * Delete time slot
   */
  async deleteTimeSlot(id: number): Promise<any> {
    return adminApi.fetch(`/reservationSection/timeSlots/${id}`, { method: 'DELETE' });
  }
}

// Create and export singleton instance
const cmsApiService = new CMSApiService();
export default cmsApiService;
